import { cn, type Position } from '@exam-room/utils';
import { Resizable } from 're-resizable';
import { useRef, useState } from 'react';

import { IconButton, OpenInNewRoundedIcon } from '../../../components';

interface ResizableLayoutProps extends React.ComponentProps<'div'> {
  left?: React.ReactNode;
  right: React.ReactNode;
}

export default function ResizableLayout({
  left,
  right,
  className,
  ...props
}: ResizableLayoutProps) {
  const resizableRef = useRef<Resizable | null>();
  const [position, setPosition] = useState<Position>('c');

  const handleResizeLeft = () => {
    let width: string, newPosition: Position;
    if (position === 'c' || position === 'cr') {
      width = '66%';
      newPosition = 'r';
    } else {
      width = '50%';
      newPosition = 'c';
    }
    resizableRef?.current?.updateSize({ width, height: '100%' });
    setPosition(newPosition);
  };

  const handleResizeRight = () => {
    let width: string, newPosition: Position;
    if (position === 'c' || position === 'cl') {
      width = '34%';
      newPosition = 'l';
    } else {
      width = '50%';
      newPosition = 'c';
    }
    resizableRef?.current?.updateSize({ width, height: '100%' });
    setPosition(newPosition);
  };

  return (
    <div
      className={cn('flex !min-h-full w-full items-start overflow-hidden', className)}
      {...props}
    >
      <Resizable
        className='flex-grow border-r-4 border-transparent before:absolute before:top-1/2 before:-right-1 before:h-[96%] before:w-1 before:-translate-y-1/2 before:bg-gray-300 before:content-[""]'
        defaultSize={{
          width: '50%',
          height: '100%',
        }}
        maxWidth='80%'
        minWidth='20%'
        ref={(node) => (resizableRef.current = node)}
        handleStyles={{
          // override handle style for unused sides
          top: { cursor: 'initial' },
          left: { cursor: 'initial' },
          bottom: { cursor: 'initial' },
          topLeft: { cursor: 'initial' },
          topRight: { cursor: 'initial' },
          bottomLeft: { cursor: 'initial' },
          bottomRight: { cursor: 'initial' },
        }}
        onResize={(_, __, el) => {
          const ratio =
            el.clientWidth / (resizableRef?.current as Resizable).getParentSize()?.width;
          const newPosition =
            ratio <= 0.34
              ? 'l'
              : ratio < 0.5
                ? 'cl'
                : ratio === 0.5
                  ? 'c'
                  : ratio < 0.66
                    ? 'cr'
                    : 'r';
          setPosition(newPosition);
        }}
      >
        <div className='no-scrollbar relative h-full w-full overflow-scroll'>
          <IconButton
            className='sticky top-4 left-[calc(100%-50px)] border bg-white shadow-md'
            onClick={handleResizeLeft}
          >
            <OpenInNewRoundedIcon className={cn('text-base', position === 'r' && 'rotate-180')} />
          </IconButton>
          {left}
        </div>
      </Resizable>
      <div className='no-scrollbar relative box-border h-full w-full overflow-scroll'>
        <IconButton
          className='sticky top-4 left-4 border bg-white shadow-md'
          onClick={handleResizeRight}
        >
          <OpenInNewRoundedIcon
            className={cn('-scale-x-100 text-base', position === 'l' && 'rotate-180')}
          />
        </IconButton>
        {right}
      </div>
    </div>
  );
}
